import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';

const API = process.env.REACT_APP_API_URL || '';

const AdminMailingPage = () => {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('send');
  const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // SMTP State
  const [smtpList, setSmtpList] = useState([]);
  const [smtpForm, setSmtpForm] = useState({ name: '', host: '', port: 587, secure: false, auth_user: '', auth_pass: '', from_name: '', from_email: '', is_default: false });
  const [editingSMTP, setEditingSMTP] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testingSmtp, setTestingSmtp] = useState(null);

  // Template State
  const [templates, setTemplates] = useState([]);
  const [tplForm, setTplForm] = useState({ name: '', subject: '', body_html: '', category: 'genel' });
  const [editingTpl, setEditingTpl] = useState(null);
  const [showTplPreview, setShowTplPreview] = useState(false);

  // Campaign State
  const [campaigns, setCampaigns] = useState([]);
  const [recipientType, setRecipientType] = useState('users');
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [customEmails, setCustomEmails] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [selectedSmtp, setSelectedSmtp] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [searchRecipient, setSearchRecipient] = useState('');
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [generatePasswords, setGeneratePasswords] = useState(false);

  // Fetch all data
  const fetchSmtp = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/mailing/smtp`, { headers });
      if (res.ok) setSmtpList(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/mailing/templates`, { headers });
      if (res.ok) setTemplates(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/mailing/campaigns`, { headers });
      if (res.ok) setCampaigns(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchRecipients = useCallback(async () => {
    try {
      const [uRes, pRes] = await Promise.all([
        fetch(`${API}/api/mailing/recipients/users`, { headers }),
        fetch(`${API}/api/mailing/recipients/partners`, { headers })
      ]);
      if (uRes.ok) setUsers(await uRes.json());
      if (pRes.ok) setPartners(await pRes.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchSmtp(); fetchTemplates(); fetchCampaigns(); fetchRecipients();
  }, []);

  // Şablon seçilince subject/body doldur
  useEffect(() => {
    if (selectedTemplate) {
      const tpl = templates.find(t => t.id === parseInt(selectedTemplate));
      if (tpl) {
        setMailSubject(tpl.subject);
        setMailBody(tpl.body_html);
      }
    }
  }, [selectedTemplate]);

  // ====== SMTP CRUD ======
  const saveSMTP = async () => {
    if (!smtpForm.host || !smtpForm.auth_user || !smtpForm.auth_pass) return alert('Host, kullanıcı ve şifre zorunludur');
    const url = editingSMTP ? `${API}/api/mailing/smtp/${editingSMTP}` : `${API}/api/mailing/smtp`;
    const method = editingSMTP ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers, body: JSON.stringify(smtpForm) });
    if (res.ok) { resetSmtpForm(); fetchSmtp(); }
  };

  const deleteSMTP = async (id) => {
    if (!window.confirm('Bu SMTP ayarını silmek istediğinize emin misiniz?')) return;
    await fetch(`${API}/api/mailing/smtp/${id}`, { method: 'DELETE', headers });
    fetchSmtp();
  };

  const testSMTP = async (id) => {
    if (!testEmail) return alert('Test e-posta adresi girin');
    setTestingSmtp(id);
    try {
      const res = await fetch(`${API}/api/mailing/smtp/${id}/test`, { method: 'POST', headers, body: JSON.stringify({ test_email: testEmail }) });
      const data = await res.json();
      alert(data.success ? 'Test e-postası gönderildi!' : 'Hata: ' + data.error);
    } catch (e) { alert('Test başarısız'); }
    setTestingSmtp(null);
  };

  const resetSmtpForm = () => {
    setEditingSMTP(null);
    setSmtpForm({ name: '', host: '', port: 587, secure: false, auth_user: '', auth_pass: '', from_name: '', from_email: '', is_default: false });
  };

  // ====== TEMPLATE CRUD ======
  const saveTemplate = async () => {
    if (!tplForm.name || !tplForm.subject || !tplForm.body_html) return alert('Ad, konu ve içerik zorunludur');
    const url = editingTpl ? `${API}/api/mailing/templates/${editingTpl}` : `${API}/api/mailing/templates`;
    const method = editingTpl ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers, body: JSON.stringify(tplForm) });
    if (res.ok) { setEditingTpl(null); setTplForm({ name: '', subject: '', body_html: '', category: 'genel' }); fetchTemplates(); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    await fetch(`${API}/api/mailing/templates/${id}`, { method: 'DELETE', headers });
    fetchTemplates();
  };

  // ====== KAMPANYA GÖNDER ======
  const sendCampaign = async () => {
    const ids = selectedIds;
    const emails = customEmails.split(/[,;\n]+/).map(e => e.trim()).filter(e => e && e.includes('@'));
    if (recipientType !== 'custom' && ids.length === 0) return alert('En az bir alıcı seçin');
    if (recipientType === 'custom' && emails.length === 0) return alert('En az bir e-posta adresi girin');
    if (!selectedSmtp) return alert('SMTP ayarı seçin');
    if (!mailSubject || !mailBody) return alert('Mail konusu ve içeriği gerekli');
    if (!window.confirm(`${recipientType === 'custom' ? emails.length : ids.length} alıcıya mail gönderilecek. Devam edilsin mi?`)) return;

    setSending(true); setSendResult(null);
    try {
      const res = await fetch(`${API}/api/mailing/campaigns/send`, {
        method: 'POST', headers,
        body: JSON.stringify({
          name: campaignName || `Kampanya ${new Date().toLocaleDateString('tr-TR')}`,
          smtp_id: parseInt(selectedSmtp),
          template_id: selectedTemplate ? parseInt(selectedTemplate) : null,
          subject: mailSubject,
          body_html: mailBody,
          recipient_type: recipientType,
          recipient_ids: recipientType !== 'custom' ? ids : [],
          recipient_emails: recipientType === 'custom' ? emails : [],
          generate_passwords: generatePasswords
        })
      });
      const data = await res.json();
      if (data.success) {
        setSendResult({ success: true, message: `${data.total} alıcıya gönderim başlatıldı!`, campaign_id: data.campaign_id });
        setSelectedIds([]); setCampaignName('');
        setTimeout(fetchCampaigns, 3000);
      } else {
        setSendResult({ success: false, message: data.error || 'Gönderim başarısız' });
      }
    } catch (e) {
      setSendResult({ success: false, message: 'Bağlantı hatası' });
    }
    setSending(false);
  };

  // Toggle recipient selection
  const toggleRecipient = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const selectAll = () => {
    const list = recipientType === 'users' ? users : partners;
    const filtered = list.filter(r => !searchRecipient || (r.email + ' ' + (r.firma || r.name || '')).toLowerCase().includes(searchRecipient.toLowerCase()));
    const allIds = filtered.map(r => r.id);
    setSelectedIds(prev => {
      const allSelected = allIds.every(id => prev.includes(id));
      return allSelected ? prev.filter(id => !allIds.includes(id)) : [...new Set([...prev, ...allIds])];
    });
  };

  const fetchCampaignDetail = async (id) => {
    if (expandedCampaign === id) { setExpandedCampaign(null); setCampaignDetail(null); return; }
    try {
      const res = await fetch(`${API}/api/mailing/campaigns/${id}`, { headers });
      if (res.ok) { setCampaignDetail(await res.json()); setExpandedCampaign(id); }
    } catch (e) { console.error(e); }
  };

  const recipients = recipientType === 'users' ? users : partners;
  const filteredRecipients = recipients.filter(r =>
    !searchRecipient || (r.email + ' ' + (r.firma || r.name || '') + ' ' + (r.yetkili || r.contact_person || '')).toLowerCase().includes(searchRecipient.toLowerCase())
  );

  const statusLabels = { draft: 'Taslak', sending: 'Gönderiliyor', completed: 'Tamamlandı', failed: 'Başarısız', cancelled: 'İptal' };
  const statusColors = { draft: 'bg-gray-100 text-gray-700', sending: 'bg-blue-100 text-blue-700', completed: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700', cancelled: 'bg-orange-100 text-orange-700' };

  if (user?.role !== 'admin' && user?.rol !== 'admin') {
    return <div className="p-8 text-center text-red-600">Yetkiniz yok</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-blue-900">Mailing Sistemi</h1>
        <p className="text-sm text-blue-500 mt-1">E-posta kampanyaları oluşturun, şablonlar yönetin ve carilerinize toplu mail gönderin</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-6 bg-white rounded-xl p-1.5 shadow-sm border border-blue-100 overflow-x-auto">
        {[
          { id: 'send', label: 'Mail Gönder', icon: '📨' },
          { id: 'templates', label: 'Şablonlar', icon: '📋' },
          { id: 'campaigns', label: 'Kampanyalar', icon: '📊' },
          { id: 'smtp', label: 'SMTP Ayarları', icon: '⚙️' }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold transition whitespace-nowrap ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-md' : 'text-blue-700 hover:bg-blue-50'}`}>
            <span className="mr-1.5">{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>

      {/* ====== MAIL GÖNDER TAB ====== */}
      {activeTab === 'send' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Sol: Alıcı Seçimi */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-blue-100 shadow-sm p-5">
            <h3 className="text-sm font-bold text-blue-900 mb-3">Alıcılar</h3>
            
            {/* Alıcı tipi */}
            <div className="flex gap-1.5 mb-3">
              {[{ id: 'users', label: 'Kullanıcılar' }, { id: 'partners', label: 'Partnerler' }, { id: 'custom', label: 'Özel' }].map(t => (
                <button key={t.id} onClick={() => { setRecipientType(t.id); setSelectedIds([]); }}
                  className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-semibold transition ${recipientType === t.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {recipientType === 'custom' ? (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">E-posta Adresleri (virgülle ayırın)</label>
                <textarea rows={8} value={customEmails} onChange={e => setCustomEmails(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="mail1@example.com, mail2@example.com" />
                <div className="text-[10px] text-gray-400 mt-1">{customEmails.split(/[,;\n]+/).filter(e => e.trim() && e.includes('@')).length} e-posta</div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <input type="text" placeholder="Ara..." value={searchRecipient} onChange={e => setSearchRecipient(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs" />
                  <button onClick={selectAll} className="px-2.5 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 transition whitespace-nowrap">
                    Tümünü Seç
                  </button>
                </div>
                <div className="text-[10px] text-blue-600 font-semibold mb-2">{selectedIds.length} seçili / {filteredRecipients.length} kayıt</div>
                <div className="max-h-[400px] overflow-y-auto space-y-1 border border-gray-100 rounded-lg p-1">
                  {filteredRecipients.map(r => (
                    <label key={r.id} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition text-xs ${selectedIds.includes(r.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'}`}>
                      <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleRecipient(r.id)}
                        className="rounded border-gray-300 text-blue-600" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 truncate">{r.firma || r.name || r.yetkili || r.contact_person || '-'}</div>
                        <div className="text-gray-400 truncate">{r.email}</div>
                      </div>
                    </label>
                  ))}
                  {filteredRecipients.length === 0 && <div className="text-center py-4 text-gray-400 text-xs">Kayıt bulunamadı</div>}
                </div>
              </>
            )}
          </div>

          {/* Sağ: Mail İçeriği */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-blue-100 shadow-sm p-5">
            <h3 className="text-sm font-bold text-blue-900 mb-3">Mail İçeriği</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Kampanya Adı</label>
                  <input type="text" value={campaignName} onChange={e => setCampaignName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Opsiyonel..." />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">SMTP Sunucu *</label>
                  <select value={selectedSmtp} onChange={e => setSelectedSmtp(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                    <option value="">Seçin...</option>
                    {smtpList.filter(s => s.is_active).map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.auth_user}){s.is_default ? ' ★' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Şablon Kullan (Opsiyonel)</label>
                <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="">Şablon kullanma</option>
                  {templates.filter(t => t.is_active).map(t => (
                    <option key={t.id} value={t.id}>{t.name} - {t.subject}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Mail Konusu *</label>
                <input type="text" value={mailSubject} onChange={e => setMailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Mail başlığı..." />
                <div className="text-[10px] text-gray-400 mt-0.5">Değişkenler: {'{{firma}}'}, {'{{yetkili}}'}, {'{{ad}}'}, {'{{email}}'}, {'{{sifre}}'}</div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Mail İçeriği (HTML) *</label>
                <textarea rows={10} value={mailBody} onChange={e => setMailBody(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono" placeholder="<h1>Merhaba {{firma}}</h1><p>...</p>" />
              </div>

              {/* Önizleme */}
              {mailBody && (
                <div>
                  <button onClick={() => setShowTplPreview(!showTplPreview)} className="text-xs text-blue-600 font-semibold hover:underline mb-1">
                    {showTplPreview ? 'Önizlemeyi Gizle' : 'Önizleme Göster'}
                  </button>
                  {showTplPreview && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-[300px] overflow-auto">
                      <div dangerouslySetInnerHTML={{ __html: mailBody }} />
                    </div>
                  )}
                </div>
              )}

              {sendResult && (
                <div className={`p-3 rounded-lg text-sm font-medium ${sendResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {sendResult.message}
                </div>
              )}

              {/* Otomatik Şifre Üretme Toggle */}
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={generatePasswords} onChange={e => setGeneratePasswords(e.target.checked)} />
                  <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                </label>
                <div>
                  <span className="text-sm font-semibold text-amber-900">Otomatik Şifre Üret</span>
                  <p className="text-[10px] text-amber-700 mt-0.5">Her alıcıya geçici şifre oluşturulur ve {'{{sifre}}'} değişkeni ile maile eklenir. Şifre 3 gün geçerlidir.</p>
                </div>
              </div>

              <button onClick={sendCampaign} disabled={sending}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition shadow-md disabled:opacity-50">
                {sending ? 'Gönderiliyor...' : `Mail Gönder (${recipientType === 'custom' ? customEmails.split(/[,;\n]+/).filter(e => e.trim() && e.includes('@')).length : selectedIds.length} alıcı)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== ŞABLONLAR TAB ====== */}
      {activeTab === 'templates' && (
        <div>
          <div className="bg-white rounded-xl border border-blue-100 shadow-sm p-5 mb-6">
            <h3 className="text-sm font-bold text-blue-900 mb-3">{editingTpl ? 'Şablon Düzenle' : 'Yeni Şablon'}</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Şablon Adı *</label>
                  <input type="text" value={tplForm.name} onChange={e => setTplForm({ ...tplForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Hoşgeldin Maili" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Konu *</label>
                  <input type="text" value={tplForm.subject} onChange={e => setTplForm({ ...tplForm, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Mail başlığı" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Kategori</label>
                  <select value={tplForm.category} onChange={e => setTplForm({ ...tplForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                    <option value="genel">Genel</option>
                    <option value="bildirim">Bildirim</option>
                    <option value="promosyon">Promosyon</option>
                    <option value="siparis">Sipariş</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">HTML İçerik *</label>
                <textarea rows={12} value={tplForm.body_html} onChange={e => setTplForm({ ...tplForm, body_html: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono" placeholder='<div style="font-family:Arial,sans-serif">...</div>' />
                <div className="text-[10px] text-gray-400 mt-0.5">Değişkenler: {'{{firma}}'}, {'{{yetkili}}'}, {'{{ad}}'}, {'{{email}}'}, {'{{sifre}}'}</div>
              </div>
              {tplForm.body_html && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-[200px] overflow-auto">
                  <div className="text-[10px] text-gray-500 mb-1 font-semibold">Önizleme:</div>
                  <div dangerouslySetInnerHTML={{ __html: tplForm.body_html }} />
                </div>
              )}
              <div className="flex gap-2 justify-end">
                {editingTpl && <button onClick={() => { setEditingTpl(null); setTplForm({ name: '', subject: '', body_html: '', category: 'genel' }); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>}
                <button onClick={saveTemplate} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition">
                  {editingTpl ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {templates.map(tpl => (
              <div key={tpl.id} className="bg-white rounded-xl border border-blue-100 shadow-sm p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 font-bold text-lg flex-shrink-0">📋</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-blue-900 text-sm">{tpl.name}</div>
                  <div className="text-xs text-gray-500">{tpl.subject}</div>
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">{tpl.category}</span>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => { setEditingTpl(tpl.id); setTplForm({ name: tpl.name, subject: tpl.subject, body_html: tpl.body_html, category: tpl.category || 'genel' }); }} className="p-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" /></svg>
                  </button>
                  <button onClick={() => deleteTemplate(tpl.id)} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                  </button>
                </div>
              </div>
            ))}
            {templates.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">Henüz şablon eklenmemiş</div>}
          </div>
        </div>
      )}

      {/* ====== KAMPANYALAR TAB ====== */}
      {activeTab === 'campaigns' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-blue-900">Gönderim Geçmişi</h3>
            <button onClick={fetchCampaigns} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100">Yenile</button>
          </div>
          <div className="space-y-2">
            {campaigns.map(c => (
              <div key={c.id} className="bg-white rounded-xl border border-blue-100 shadow-sm overflow-hidden">
                <button onClick={() => fetchCampaignDetail(c.id)} className="w-full p-4 flex items-center gap-4 text-left hover:bg-blue-50/30 transition">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-blue-900 text-sm">{c.name || 'Kampanya'}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusColors[c.status]}`}>{statusLabels[c.status]}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{c.subject}</div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                      <span>SMTP: {c.smtp_name || '-'}</span>
                      <span>Şablon: {c.template_name || 'Özel'}</span>
                      <span>{new Date(c.created_at).toLocaleString('tr-TR')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-900">{c.total_count}</div>
                      <div className="text-[10px] text-gray-400">Toplam</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{c.sent_count}</div>
                      <div className="text-[10px] text-gray-400">Gönderildi</div>
                    </div>
                    {c.failed_count > 0 && (
                      <div className="text-center">
                        <div className="text-lg font-bold text-red-600">{c.failed_count}</div>
                        <div className="text-[10px] text-gray-400">Başarısız</div>
                      </div>
                    )}
                    <svg className={`w-5 h-5 text-blue-400 transition-transform ${expandedCampaign === c.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
                  </div>
                </button>
                {expandedCampaign === c.id && campaignDetail && (
                  <div className="px-4 pb-4 border-t border-blue-50">
                    <div className="mt-3 max-h-[300px] overflow-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-1.5 text-left font-semibold text-gray-600">#</th>
                            <th className="px-3 py-1.5 text-left font-semibold text-gray-600">E-posta</th>
                            <th className="px-3 py-1.5 text-left font-semibold text-gray-600">Ad</th>
                            <th className="px-3 py-1.5 text-left font-semibold text-gray-600">Durum</th>
                            <th className="px-3 py-1.5 text-left font-semibold text-gray-600">Hata</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(campaignDetail.logs || []).map((log, i) => (
                            <tr key={log.id} className="border-t border-gray-100">
                              <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                              <td className="px-3 py-1.5 text-gray-800">{log.recipient_email}</td>
                              <td className="px-3 py-1.5 text-gray-600">{log.recipient_name}</td>
                              <td className="px-3 py-1.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${log.status === 'sent' ? 'bg-green-100 text-green-700' : log.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {log.status === 'sent' ? 'Gönderildi' : log.status === 'failed' ? 'Başarısız' : 'Bekliyor'}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-red-500 text-[10px] max-w-[200px] truncate">{log.error_message || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {campaigns.length === 0 && <div className="text-center py-12 text-gray-400 text-sm">Henüz kampanya gönderilmemiş</div>}
          </div>
        </div>
      )}

      {/* ====== SMTP AYARLARI TAB ====== */}
      {activeTab === 'smtp' && (
        <div>
          <div className="bg-white rounded-xl border border-blue-100 shadow-sm p-5 mb-6">
            <h3 className="text-sm font-bold text-blue-900 mb-3">{editingSMTP ? 'SMTP Düzenle' : 'Yeni SMTP Ekle'}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Ad</label>
                <input type="text" value={smtpForm.name} onChange={e => setSmtpForm({ ...smtpForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Gmail SMTP" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Host *</label>
                <input type="text" value={smtpForm.host} onChange={e => setSmtpForm({ ...smtpForm, host: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="smtp.gmail.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Port</label>
                <input type="number" value={smtpForm.port} onChange={e => setSmtpForm({ ...smtpForm, port: parseInt(e.target.value) || 587 })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div className="flex items-end gap-3">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={smtpForm.secure} onChange={e => setSmtpForm({ ...smtpForm, secure: e.target.checked })} className="rounded border-gray-300 text-blue-600" />
                  SSL/TLS
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={smtpForm.is_default} onChange={e => setSmtpForm({ ...smtpForm, is_default: e.target.checked })} className="rounded border-gray-300 text-blue-600" />
                  Varsayılan
                </label>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Kullanıcı *</label>
                <input type="text" value={smtpForm.auth_user} onChange={e => setSmtpForm({ ...smtpForm, auth_user: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="user@gmail.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Şifre *</label>
                <input type="password" value={smtpForm.auth_pass} onChange={e => setSmtpForm({ ...smtpForm, auth_pass: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="App password" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Gönderen Adı</label>
                <input type="text" value={smtpForm.from_name} onChange={e => setSmtpForm({ ...smtpForm, from_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Order INVAMED" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Gönderen E-posta</label>
                <input type="text" value={smtpForm.from_email} onChange={e => setSmtpForm({ ...smtpForm, from_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="info@invamed.com" />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              {editingSMTP && <button onClick={resetSmtpForm} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>}
              <button onClick={saveSMTP} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition">
                {editingSMTP ? 'Güncelle' : 'Ekle'}
              </button>
            </div>
          </div>

          {/* Test Email */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="text-lg">🧪</span>
              <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                className="flex-1 px-3 py-2 border border-yellow-300 rounded-lg text-sm" placeholder="Test e-posta adresi..." />
              <div className="text-[10px] text-yellow-700">SMTP'yi test etmek için bir adres girin ve ilgili SMTP'nin test butonuna tıklayın</div>
            </div>
          </div>

          {/* SMTP List */}
          <div className="space-y-2">
            {smtpList.map(s => (
              <div key={s.id} className={`bg-white rounded-xl border shadow-sm p-4 flex items-center gap-4 ${s.is_active ? 'border-blue-100' : 'border-gray-200 opacity-60'}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg flex-shrink-0 ${s.is_default ? 'bg-yellow-50' : 'bg-blue-50'}`}>
                  {s.is_default ? '⭐' : '📧'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-blue-900 text-sm">{s.name}</span>
                    {s.is_default && <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded font-semibold">Varsayılan</span>}
                  </div>
                  <div className="text-xs text-gray-500">{s.host}:{s.port} | {s.auth_user}</div>
                  <div className="text-[10px] text-gray-400">Gönderen: {s.from_name} &lt;{s.from_email}&gt;</div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={() => testSMTP(s.id)} disabled={testingSmtp === s.id}
                    className="px-2.5 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-xs font-semibold hover:bg-yellow-100 transition disabled:opacity-50">
                    {testingSmtp === s.id ? '...' : 'Test'}
                  </button>
                  <button onClick={() => { setEditingSMTP(s.id); setSmtpForm({ name: s.name, host: s.host, port: s.port, secure: !!s.secure, auth_user: s.auth_user, auth_pass: '', from_name: s.from_name, from_email: s.from_email, is_default: !!s.is_default }); }}
                    className="p-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" /></svg>
                  </button>
                  <button onClick={() => deleteSMTP(s.id)} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                  </button>
                </div>
              </div>
            ))}
            {smtpList.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">Henüz SMTP ayarı eklenmemiş</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMailingPage;
